import React, { useCallback, useRef, useEffect } from "react";
import { FixedSizeList as List } from "react-window";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const CELL_WIDTH = 120;
const CELL_HEIGHT = 40;

const getExcelColumnLabel = (index) => {
  let label = "";
  while (index >= 0) {
    label = String.fromCharCode((index % 26) + 65) + label;
    index = Math.floor(index / 26) - 1;
  }
  return label;
};

const VirtualizedExcelInput = ({ tableData, setTableData, height: outerHeight }) => {
  const boxHeight = outerHeight || 540;
  const listHeight = boxHeight - 40;
  const scrollContainerRef = useRef(null);
  const headerRef = useRef(null);
  const totalWidth = Math.max((tableData[0]?.length || 1) * CELL_WIDTH, 500);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const header = headerRef.current;
    if (container && header) {
      const sync = () => {
        header.scrollLeft = container.scrollLeft;
      };
      container.addEventListener("scroll", sync);
      return () => container.removeEventListener("scroll", sync);
    }
  }, []);

  const handleExcelPaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData.getData("Text");
    const rows = clipboardData.trim().split("\n");
    const parsedData = rows.map((row) => row.split("\t"));
    const maxCols = Math.max(...parsedData.map((r) => r.length));

    const result = parsedData.map((row) =>
      Array.from({ length: maxCols }, (_, i) => row[i] ?? "")
    );
    setTableData(result);
  };

  const handleCellChange = useCallback(
    (rowIndex, colIndex, value) => {
      setTableData((prev) => {
        const updated = [...prev];
        const row = [...updated[rowIndex]];
        row[colIndex] = value;
        updated[rowIndex] = row;
        return updated;
      });
    },
    [setTableData]
  );

  const clearTable = () => setTableData([]);

  const exportToCSV = () => {
    const csvContent = tableData
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "excel_data.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const Row = ({ index, style }) => {
    const row = tableData[index];
    return (
      <div style={{ ...style, display: "flex" }}>
        {row.map((cell, colIndex) => (
          <div
            key={colIndex}
            style={{
              width: CELL_WIDTH,
              height: CELL_HEIGHT,
              border: "1px solid #ccc",
              boxSizing: "border-box",
              padding: 2,
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={cell}
              onChange={(e) =>
                handleCellChange(index, colIndex, e.target.value)
              }
            />
          </div>
        ))}
      </div>
    );
  };

  const renderColumnHeaders = () => {
    if (!tableData[0]) return null;
    return (
      <div
        ref={headerRef}
        style={{
          display: "flex",
          width: totalWidth,
          position: "sticky",
          top: 0,
          background: "#fff",
          zIndex: 1,
          borderBottom: "1px solid #ccc",
        }}
      >
        {tableData[0].map((_, i) => (
          <div
            key={i}
            style={{
              width: CELL_WIDTH,
              height: CELL_HEIGHT,
              lineHeight: `${CELL_HEIGHT}px`,
              textAlign: "center",
              fontWeight: "bold",
              borderRight: "1px solid #ccc",
              background: "#f0f0f0",
              boxSizing: "border-box",
            }}
          >
            {getExcelColumnLabel(i)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Box mt={4}>
      <Box mb={2} display="flex" gap={2}>
        <Button variant="outlined" color="secondary" onClick={clearTable}>
          Clear Table
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={exportToCSV}
          startIcon={<FileDownloadIcon />}
        >
          Export CSV
        </Button>
      </Box>

      <Typography variant="body2" mb={1}>
        Paste Excel Table (Virtualized & Editable)
      </Typography>

      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: "4px",
          height: boxHeight,
          overflow: "hidden",
        }}
        onPaste={handleExcelPaste}
        tabIndex={0}
      >
        <div
          ref={scrollContainerRef}
          style={{
            width: "100%",
            height: "100%",
            overflow: "auto",
          }}
        >
          <div style={{ width: totalWidth }}>
            {renderColumnHeaders()}
            <List
              height={listHeight}
              itemCount={tableData.length}
              itemSize={CELL_HEIGHT}
              width={totalWidth}
            >
              {Row}
            </List>
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default VirtualizedExcelInput;
